import axios from 'axios'

// 创建axios实例
const api = axios.create({
  baseURL: '/api', // 使用相对路径，代理配置会处理跨域
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
api.interceptors.request.use(
  config => {
    // 在发送请求之前做些什么
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    // 对请求错误做些什么
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  response => {
    // 对响应数据做些什么
    return response
  },
  error => {
    // 对响应错误做些什么
    const res = error.response
    if (res && res.status === 403 && res.data && res.data.code === 'PASSWORD_EXPIRED') {
      localStorage.removeItem('access_token')
      localStorage.removeItem('user')
      if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
        const p = window.location.pathname || ''
        if (!p.includes('password-unlock')) {
          window.location.href = '/password-unlock'
        }
      }
    } else if (res && res.status === 401) {
      // token过期或无效，清除本地存储并跳转到登录页
      localStorage.removeItem('access_token')
      localStorage.removeItem('user')
    }
    return Promise.reject(error)
  }
)

// 课程相关API
export const getCourses = () => api.get('/courses')
export const createCourse = (courseData) => api.post('/courses', courseData)
export const getCourseDetail = (courseId) => api.get(`/courses/${courseId}`)
export const enrollCourse = (courseId) => api.post('/courses/enroll', { course_id: courseId })
export const unenrollCourse = (courseId) => api.post('/courses/unenroll', { course_id: courseId })
export const getMyCourses = () => api.get('/courses/my-courses')
export const getCourseExams = (courseId) => api.get(`/courses/${courseId}/exams`)
export const getCourseProgress = (courseId) => api.get(`/courses/${courseId}/progress`)
export const updateCourseProgress = (courseId, progressData) => api.post(`/courses/${courseId}/progress`, progressData)
export const startLearningCourse = (courseId) => api.post(`/courses/${courseId}/start-learning`)

// 文档相关API
export const getDocuments = (params = {}) => api.get('/documents', { params })
export const getPublishedDocuments = () => api.get('/documents/published')
export const uploadDocument = (documentData) => {
  // 如果是FormData，使用multipart/form-data
  const config = documentData instanceof FormData ? {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    timeout: 600000  // 大文件上传：10 分钟超时（与后端 MAX_UPLOAD_MB 配合）
  } : {}
  return api.post('/documents', documentData, config)
}

export const publishDocumentToHomepage = (documentId) => api.put(`/documents/${documentId}/publish`)

// 分类管理API
export const getCategories = () => api.get('/documents/categories')
export const createCategory = (categoryData) => api.post('/documents/categories', categoryData)
export const updateCategory = (categoryId, categoryData) => api.put(`/documents/categories/${categoryId}`, categoryData)
export const deleteCategory = (categoryId) => api.delete(`/documents/categories/${categoryId}`)
export const getDocumentById = (documentId) => api.get(`/documents/${documentId}`)

// 文档树和文件夹管理API
export const getDocumentTree = () => api.get('/documents/tree')
export const createDocumentFolder = (folderData) => api.post('/documents/folders', folderData)
/** 上传 ZIP，自动解压并创建文档；formData 需包含 file，可选 parent_folder_id、new_folder_name、course_id、category_id */
export const uploadZipDocument = (formData) => api.post('/documents/upload-zip', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 })
export const renameDocument = (documentId, documentData) => api.put(`/documents/${documentId}`, documentData)
export const updateDocument = (documentId, documentData) => api.put(`/documents/${documentId}`, documentData)
export const deleteDocument = (documentId, params = {}) => api.delete(`/documents/${documentId}`, { params })

// 考试相关API
export const getExams = () => api.get('/exams')
export const getQuestions = (params = {}) => api.get('/questions', { params })
export const getQuestionDetail = (questionId) => api.get(`/questions/${questionId}`)
export const batchUpdateQuestionVisibility = (ids, isPublic) =>
  api.post('/questions/batch-update-visibility', { ids, is_public: isPublic })
export const batchMoveQuestions = (ids, categoryId) =>
  api.post('/questions/batch-move', { ids, category_id: categoryId })
export const createExam = (examData) => api.post('/exams', examData)
export const updateExam = (examId, examData) => api.put(`/exams/${examId}`, examData)
export const deleteExam = (examId) => api.delete(`/exams/${examId}`)
export const getExamDetail = (examId) => api.get(`/exams/${examId}`)
export const startExam = (examId, retake = false) => api.post(`/exams/${examId}/start`, { retake })
export const submitExam = (examId, answers, resultId = null) => api.post('/exam-results', { exam_id: examId, answers, result_id: resultId })
export const getExamResult = (resultId) => api.get(`/exam-results/${resultId}`)
export const exportExamResults = (params = {}) => api.get('/exam-results/export', {
  params,
  responseType: 'blob',
  timeout: 120000
})

/** Word（.docx）→ 章节预览 / 通义出题 → 下载 xlsx，与批量导入列格式一致；仅教师/管理员 */
export const previewQuestionBankWord = (formData) =>
  api.post('/question-gen/word/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000
  })
export const recommendQuestionBankWordPlan = (formData) =>
  api.post('/question-gen/word/preview/recommend', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000
  })
export const generateQuestionBankFromWord = (formData) =>
  api.post('/question-gen/word', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000
  })
export const getQuestionBankGenerationTask = (taskId) => api.get(`/question-gen/tasks/${taskId}`)
export const getQuestionBankGenerationDrafts = (taskId, params = {}) =>
  api.get(`/question-gen/tasks/${taskId}/drafts`, { params })
export const cancelQuestionBankGenerationTask = (taskId) => api.post(`/question-gen/tasks/${taskId}/cancel`)
export const patchQuestionBankGenerationDraft = (taskId, draftId, data) =>
  api.patch(`/question-gen/tasks/${taskId}/drafts/${draftId}`, data)
export const bulkUpdateQuestionBankGenerationDrafts = (taskId, action, draftIds) =>
  api.post(`/question-gen/tasks/${taskId}/drafts/bulk`, { action, draft_ids: draftIds })
export const retryQuestionBankGenerationDrafts = (taskId, stage, draftIds) =>
  api.post(`/question-gen/tasks/${taskId}/retry`, { stage, draft_ids: draftIds })
export const validateQuestionBankGenerationDrafts = (taskId, data = {}) =>
  api.post(`/question-gen/tasks/${taskId}/validate`, data, { timeout: 120000 })
export const cancelQuestionBankGenerationValidation = (taskId) =>
  api.post(`/question-gen/tasks/${taskId}/validate/cancel`, {}, { timeout: 30000 })
export const validateQuestionBankGenerationDraft = (taskId, draftId, data = {}) =>
  api.post(`/question-gen/tasks/${taskId}/drafts/${draftId}/validate`, data, { timeout: 120000 })
export const saveQuestionBankGenerationDrafts = (taskId, drafts) =>
  Promise.all((drafts || []).map((draft) => patchQuestionBankGenerationDraft(
    taskId,
    draft.draft_id || draft.id,
    { ...draft, version: draft.version }
  )))
export const commitQuestionBankGenerationDrafts = (taskId, draftIds) =>
  api.post(`/question-gen/tasks/${taskId}/commit`, { draft_ids: draftIds }, { timeout: 120000 })
export const downloadQuestionBankGenerationResult = (taskId) =>
  api.get(`/question-gen/tasks/${taskId}/download`, {
    responseType: 'blob',
    timeout: 600000
  })

// 用户管理相关API
export const getUsers = (params = {}) => api.get('/usermgmt/users', { params })
export const createUser = (userData) => api.post('/usermgmt/users', userData)
export const getTransportPublicKey = () => api.get('/crypto/transport-public-key')

// 数据看板相关API
export const getStudentProfiles = (params = {}) => api.get('/dashboard/students', { params })
export const getStudentDetail = (studentId) => api.get(`/dashboard/students/${studentId}/detail`)
export const getTeacherProfiles = (params = {}) => api.get('/dashboard/teachers', { params })
export const getCourseRanking = (params = {}) => api.get('/dashboard/courses/ranking', { params })
export const exportStudentProfiles = () => api.get('/dashboard/students/export', { responseType: 'blob', headers: { 'Accept': 'text/csv' } })
export const exportTeacherProfiles = () => api.get('/dashboard/teachers/export', { responseType: 'blob', headers: { 'Accept': 'text/csv' } })
export const exportCourseRanking = () => api.get('/dashboard/courses/ranking/export', { responseType: 'blob', headers: { 'Accept': 'text/csv' } })
export const updateUser = (userId, userData) => api.put(`/usermgmt/users/${userId}`, userData)
export const updateUserPermissions = (userId, permissions) => api.patch(`/usermgmt/users/${userId}/permissions`, { permissions })
export const deleteUser = (userId) => api.delete(`/usermgmt/users/${userId}`)
export const changeUserPassword = (userId, passwordData) => api.put(`/usermgmt/users/${userId}/password`, passwordData)
export const approveUser = (userId) => api.post(`/usermgmt/users/${userId}/approve`)
export const rejectUser = (userId) => api.post(`/usermgmt/users/${userId}/reject`)
export const getAutoApproveConfig = () => api.get('/usermgmt/system/config/auto-approve')
export const setAutoApproveConfig = (autoApprove) => api.put('/usermgmt/system/config/auto-approve', { auto_approve: autoApprove })

export const getPasswordUnlockRequests = (params = {}) => api.get('/usermgmt/password-unlock-requests', { params })
export const approvePasswordUnlock = (id) => api.post(`/usermgmt/password-unlock-requests/${id}/approve`)
export const rejectPasswordUnlock = (id, data) => api.post(`/usermgmt/password-unlock-requests/${id}/reject`, data || {})

// 发送短信验证码
export const sendSmsCode = (phone) => api.post('/auth/send-sms', { phone })

// 教师端课程管理相关API
// 课程管理
export const getTeacherCourses = (params = {}) => api.get('/teacher/courses', { params })
export const getTeacherCourseDetail = (courseId) => api.get(`/teacher/courses/${courseId}`)
export const createTeacherCourse = (courseData) => api.post('/teacher/courses', courseData)
export const updateTeacherCourse = (courseId, courseData) => api.put(`/teacher/courses/${courseId}`, courseData)
export const deleteTeacherCourse = (courseId) => api.delete(`/teacher/courses/${courseId}`)
export const publishTeacherCourse = (courseId) => api.post(`/teacher/courses/${courseId}/publish`)
export const unpublishTeacherCourse = (courseId) => api.post(`/teacher/courses/${courseId}/unpublish`)

// 教师端直播课程管理API
export const getTeacherLiveCourses = (params = {}) => api.get('/teacher/courses', { params: { ...params, type: 'live' } })
export const createTeacherLiveCourse = (courseData) => api.post('/teacher/courses', { ...courseData, course_type: 'live' })
export const updateTeacherLiveCourse = (courseId, courseData) => api.put(`/teacher/courses/${courseId}`, { ...courseData, course_type: 'live' })
export const deleteTeacherLiveCourse = (courseId) => api.delete(`/teacher/courses/${courseId}`)
export const toggleTeacherLiveCourseStatus = (courseId) => api.post(`/teacher/courses/${courseId}/toggle-status`)
export const startTeacherLiveCourse = (courseId) => api.post(`/teacher/courses/${courseId}/start-live`)
export const endTeacherLiveCourse = (courseId) => api.post(`/teacher/courses/${courseId}/end-live`)
export const uploadTeacherReplay = (replayData) => api.post(`/teacher/courses/${replayData.courseId}/upload-replay`, replayData)

// 课件资源库管理
export const getTeacherResources = (params = {}) => api.get('/teacher/resources', { params })
export const createTeacherResource = (resourceData) => api.post('/teacher/resources', resourceData)
export const updateTeacherResource = (resourceId, resourceData) => api.put(`/teacher/resources/${resourceId}`, resourceData)
export const deleteTeacherResource = (resourceId) => api.delete(`/teacher/resources/${resourceId}`)

// 资源库管理API（兼容ResourceLibrary组件）
export const getResources = (params = {}) => api.get('/teacher/resources', { params })
export const createFolder = (folderData) => api.post('/teacher/resources', folderData)
export const renameResource = (resourceId, resourceData) => api.put(`/teacher/resources/${resourceId}`, resourceData)
export const deleteResource = (resourceId) => api.delete(`/teacher/resources/${resourceId}`)
export const uploadResource = (fileData) => {
  const formData = new FormData()
  formData.append('file', fileData.file)
  formData.append('title', fileData.title || fileData.file.name)
  formData.append('parent_folder_id', fileData.parent_folder_id || '')
  
  return api.post('/teacher/resources/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

// 课程考试关联管理
export const associateCourseExam = (courseId, examData) => api.post(`/teacher/courses/${courseId}/exams`, examData)
export const removeCourseExamAssociation = (courseId, associationId) => api.delete(`/teacher/courses/${courseId}/exams/${associationId}`)

// 首页相关API
export const getRecommendedCourses = (params = {}) => api.get('/homepage/recommended-courses', { params })
export const getPolicies = (params = {}) => api.get('/homepage/policies', { params })
export const getStudentRanking = (params = {}) => api.get('/homepage/student-ranking', { params })
export const getWorkGuidance = () => api.get('/homepage/work-guidance')
export const getWorkGuidanceAdmin = () => api.get('/usermgmt/system/config/work-guidance')
export const updateWorkGuidance = (config) => api.put('/usermgmt/system/config/work-guidance', { config })
export const getAnnouncement = (scene = 'home') => api.get('/homepage/announcement', { params: { scene } })
export const getAnnouncementAdmin = () => api.get('/usermgmt/system/config/announcement')
export const updateAnnouncement = (config) => api.put('/usermgmt/system/config/announcement', { config })
export const getRecommendedCourseware = (params = {}) => api.get('/homepage/recommended-courseware', { params })
export const getRecommendedVideos = (params = {}) => api.get('/homepage/recommended-videos', { params })

// 案例库
export const getCaseFiles = (params = {}) => api.get('/case-files', { params })
export const getCaseFile = (id) => api.get(`/case-files/${id}`)
export const uploadCaseFile = (formData) => api.post('/case-files', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
})
export const createCaseFolder = (data) => api.post('/case-files', data)
export const updateCaseFile = (id, data) => api.put(`/case-files/${id}`, data)
export const deleteCaseFile = (id) => api.delete(`/case-files/${id}`)

// 会议相关API
export const getMeetings = () => api.get('/meetings')
export const getMeetingDetail = (meetingId) => api.get(`/meetings/${meetingId}`)
export const createMeeting = (meetingData) => api.post('/meetings', meetingData)
export const updateMeeting = (meetingId, meetingData) => api.put(`/meetings/${meetingId}`, meetingData)
export const deleteMeeting = (meetingId) => api.delete(`/meetings/${meetingId}`)

// 我的证书 API
export const getMyCertificates = () => api.get('/certificates/my')
export const getMyCertificateDetail = (certificateId) => api.get(`/certificates/my/${certificateId}`)

export default api
